import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

const ViewSubmission = () => {
  const [submission, setSubmission] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');
  const [isTaskCaptain, setIsTaskCaptain] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchUserInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to fetch submission details');
        return;
      }

      const response = await axios.get(API_ENDPOINTS.ME, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserInfo(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        console.error('Error fetching user info:', error);
      }
    }
  }, [navigate]);

  const fetchSubmission = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to fetch submission details');
        return;
      }

      const response = await axios.get(`${API_ENDPOINTS.SUBMISSIONS}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSubmission(response.data);
    } catch (error) {
      // Handle 401 Unauthorized errors
      if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      
      // Handle other errors
      if (error.response?.status === 403) {
        setError('You are not authorized to view this submission');
      } else if (error.response?.status === 404) {
        setError('Submission not found');
      } else {
        setError('Failed to fetch submission details');
        console.error('Error fetching submission:', error);
      }
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchUserInfo();
    fetchSubmission();
  }, [fetchUserInfo, fetchSubmission]);

  useEffect(() => {
    if (submission && userInfo) {
      // Check if user is task captain for this submission's task
      const isCaptain = userInfo.roles?.some(role => 
        role.role_type === 'task_captain' && role.task_name === submission.task_name
      );
      setIsTaskCaptain(isCaptain);
    }
  }, [submission, userInfo]);

  const getFileUrl = useCallback((filePath) => {
    // Extract file type and filename from the path
    const parts = filePath.split('/');
    const filename = parts[parts.length - 1];
    
    // Use the general file endpoint for both owners and task captains
    return `${API_ENDPOINTS.FILES}/${parts[parts.length - 2].replace('s', '')}/${filename}`;
  }, []);

  const handleFileDownload = async (filePath, fileType) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to download files');
        return;
      }

      // Extract file type and filename from the path
      const parts = filePath.split('/');
      const filename = parts[parts.length - 1];
      const fileTypeFromPath = parts[parts.length - 2].replace('s', '');
      
      // Get signed URL from backend
      const response = await axios.get(`${API_ENDPOINTS.FILES}/${fileTypeFromPath}/${filename}/signed-url`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const { signed_url } = response.data;
      
      // Navigate to the signed URL directly (this triggers browser download)
      // The signed URL is relative, so we need to construct the full URL
      const fullUrl = `${window.location.origin}/submission/api/${signed_url}`;
      window.location.href = fullUrl;
      
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file. Please try again.');
    }
  };

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {error}
            </Typography>
            {(error.includes('session has expired') || error.includes('must be logged in')) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ mt: 1, mr: 1 }}
              >
                Go to Login
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 1 }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  if (!submission) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Submission Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isTaskCaptain && (
              <Chip 
                label="Task Captain View" 
                color="primary" 
                variant="outlined"
                size="small"
              />
            )}
            {!isTaskCaptain && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/submissions/${id}/edit`)}
              >
                Edit Submission
              </Button>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Name</Typography>
            <Typography>{submission.name}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Task</Typography>
            <Typography>{submission.task_name}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Year</Typography>
            <Typography>{submission.year}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Created At</Typography>
            <Typography>
              {new Date(submission.created_at).toLocaleString()}
            </Typography>
          </Grid>

          {isTaskCaptain && (
            <Grid item xs={12}>
              <Typography variant="h6">Owner ID</Typography>
              <Typography>{submission.owner_id}</Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="h6">Files</Typography>
            <Box sx={{ mt: 1 }}>
              <Button
                onClick={() => handleFileDownload(submission.pdf_path, 'pdf')}
                sx={{ display: 'block', mb: 1 }}
              >
                Download Extended Abstract (PDF)
              </Button>
              <Button
                onClick={() => handleFileDownload(submission.zip_path, 'zip')}
              >
                Download Submission Files (ZIP)
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ViewSubmission; 